import React from 'react';
import Card from '../../../components/ui/Card';
import { fmtBRL, fmtNum } from '../utils';

export default function CapitalChangesTab({ lang, year, petrobrasData, refDate, t }) {
  const aumentoRows   = petrobrasData['capital_social_aumento'] || [];
  const aumentoClRows = petrobrasData['capital_social_aumento_classe_acao'] || [];
  const reducaoRows   = petrobrasData['capital_social_reducao'] || [];
  const desdobRows    = petrobrasData['capital_social_desdobramento'] || [];

  const fallbackBadge = () => null;

  const emptyRow = (cols) => (
    <tr><td colSpan={cols} className="p-8 text-center text-[var(--muted)]">{t.l_empty}</td></tr>
  );

  const totalAumentado  = aumentoRows.reduce((s, r) => s + (parseFloat(r.Valor_Total_Emissao) || 0), 0);
  const totalReducido   = reducaoRows.reduce((s, r) => s + (parseFloat(r.Valor_Reducao) || 0), 0);
  const totalDesdobrado = desdobRows.length;

  return (
    <div className="space-y-6">

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: lang === 'pt' ? 'Total Aumentos' : 'Total Increases', value: fmtBRL(totalAumentado), color: 'text-emerald-500', count: aumentoRows.length },
          { label: lang === 'pt' ? 'Total Reduções' : 'Total Reductions', value: totalReducido > 0 ? fmtBRL(totalReducido) : '-', color: 'text-red-500', count: reducaoRows.length },
          { label: lang === 'pt' ? 'Desdobramentos' : 'Stock Splits', value: totalDesdobrado > 0 ? `${totalDesdobrado} eventos` : '-', color: 'text-blue-400', count: desdobRows.length },
        ].map((kpi, i) => (
          <div key={i} className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-4 space-y-1">
            <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block">{kpi.label}</span>
            <span className={`text-lg font-black block ${kpi.color}`}>{kpi.value}</span>
            <span className="text-[9px] text-[var(--muted)]">{kpi.count} {lang === 'pt' ? 'eventos' : 'events'}</span>
          </div>
        ))}
      </div>

      {/* Capital Increases */}
      <Card
        title={lang === 'pt' ? 'Aumentos de Capital' : 'Capital Increases'}
        subtitle={lang === 'pt' ? `${aumentoRows.length} eventos registrados` : `${aumentoRows.length} registered events`}
        refDate={refDate}
      >
        {fallbackBadge()}
        <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs min-w-[700px]">
            <thead>
              <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                <th className="p-3 font-semibold">{lang === 'pt' ? 'Data Deliberação' : 'Deliberation Date'}</th>
                <th className="p-3 font-semibold">{lang === 'pt' ? 'Órgão' : 'Authority'}</th>
                <th className="p-3 font-semibold">{lang === 'pt' ? 'Tipo Subscrição' : 'Subscription Type'}</th>
                <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Valor Total' : 'Total Value'}</th>
                <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Ações ON' : 'Common Shares'}</th>
                <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Ações PN' : 'Preferred Shares'}</th>
                <th className="p-3 font-semibold">{lang === 'pt' ? 'Forma Integralização' : 'Payment Form'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {aumentoRows.length > 0 ? aumentoRows.map((r, idx) => (
                <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                  <td className="p-3 font-mono text-yellow-500">{r.Data_Deliberacao || '-'}</td>
                  <td className="p-3 text-[var(--muted)] max-w-[160px] truncate" title={r.Orgao_Deliberacao_Aumento}>{r.Orgao_Deliberacao_Aumento || '-'}</td>
                  <td className="p-3">
                    <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                      {r.Tipo_Subscricao || '-'}
                    </span>
                  </td>
                  <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(r.Valor_Total_Emissao)}</td>
                  <td className="p-3 text-right text-[var(--text)]">{fmtNum(r.Quantidade_Acoes_Ordinarias)}</td>
                  <td className="p-3 text-right text-[var(--text)]">{fmtNum(r.Quantidade_Acoes_Preferenciais)}</td>
                  <td className="p-3 text-[var(--muted)]">{r.Forma_Integralizacao || '-'}</td>
                </tr>
              )) : emptyRow(7)}
              {aumentoClRows.length > 0 && (
                <tr className="bg-[var(--accent-soft)]/10">
                  <td colSpan={7} className="p-2 text-[10px] text-[var(--muted)] font-semibold px-3">
                    + {aumentoClRows.length} {lang === 'pt' ? 'registros de classe de ação associados' : 'associated share class records'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Capital Reductions */}
      <Card
        title={lang === 'pt' ? 'Reduções de Capital' : 'Capital Reductions'}
        subtitle={reducaoRows.length > 0
          ? (lang === 'pt' ? `${reducaoRows.length} eventos registrados` : `${reducaoRows.length} registered events`)
          : (lang === 'pt' ? 'Nenhum evento no histórico FRE' : 'No events in FRE history')}
        refDate={refDate}
      >
        {reducaoRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
            <span className="text-3xl">—</span>
            <p className="text-xs text-[var(--muted)]">
              {lang === 'pt'
                ? 'Nenhuma redução de capital registrada no FRE da Petrobras (2015–2025).'
                : 'No capital reduction registered in Petrobras FRE filings (2015–2025).'}
            </p>
          </div>
        ) : (
          <>
            {fallbackBadge()}
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[500px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Data Deliberação' : 'Deliberation Date'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Órgão' : 'Authority'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Valor Redução' : 'Reduction Value'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Motivo' : 'Reason'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {reducaoRows.map((r, idx) => (
                    <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                      <td className="p-3 font-mono text-yellow-500">{r.Data_Deliberacao || '-'}</td>
                      <td className="p-3 text-[var(--muted)]">{r.Orgao_Deliberacao_Reducao || '-'}</td>
                      <td className="p-3 text-right font-bold text-red-500">{fmtBRL(r.Valor_Reducao)}</td>
                      <td className="p-3 text-[var(--muted)]">{r.Motivo_Reducao || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Stock Splits */}
      <Card
        title={lang === 'pt' ? 'Desdobramentos de Ações' : 'Stock Splits'}
        subtitle={desdobRows.length > 0
          ? (lang === 'pt' ? `${desdobRows.length} eventos registrados` : `${desdobRows.length} registered events`)
          : (lang === 'pt' ? 'Nenhum evento no histórico FRE' : 'No events in FRE history')}
        refDate={refDate}
      >
        {desdobRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
            <span className="text-3xl">—</span>
            <p className="text-xs text-[var(--muted)]">
              {lang === 'pt'
                ? 'Nenhum desdobramento de ações registrado no FRE da Petrobras (2015–2025).'
                : 'No stock split registered in Petrobras FRE filings (2015–2025).'}
            </p>
          </div>
        ) : (
          <>
            {fallbackBadge()}
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[600px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Data Deliberação' : 'Deliberation Date'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Órgão' : 'Authority'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Ações Antes' : 'Shares Before'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Ações Depois' : 'Shares After'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Fator' : 'Split Ratio'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {desdobRows.map((r, idx) => (
                    <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                      <td className="p-3 font-mono text-yellow-500">{r.Data_Deliberacao || '-'}</td>
                      <td className="p-3 text-[var(--muted)]">{r.Orgao_Deliberacao || '-'}</td>
                      <td className="p-3 text-right text-[var(--text)]">{fmtNum(r.Quantidade_Acoes_Antes)}</td>
                      <td className="p-3 text-right text-[var(--text)]">{fmtNum(r.Quantidade_Acoes_Depois)}</td>
                      <td className="p-3 text-right font-bold text-blue-400">{r.Fator_Desdobramento || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

    </div>
  );
}
